import { useNuxtApp } from 'nuxt/app';
import { FetchingServices, type FetchingResponse } from '~/plugins/fetching';
import { storeFactory } from '~/stores';
import { useToastStore } from '~/stores/toast';

export { FetchingServices, useToastStore as Toast };
export type { FetchingResponse };

export class Base {
  public async get<G>(
    service: FetchingServices,
    url: string,
    timeout: number = 0,
    withToken: boolean = true,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    return await useNuxtApp()
      .$fetching.get<G>(service, url, withToken, timeout, responseType)
      .then((resp) => {
        if (resp.status && this.resIsNotOk(resp.status)) {
          this.showServiceError(resp.status, service);
        }
        return resp;
      })
      .catch((err) => {
        this.showServiceCatchError();
        throw err;
      });
  }
  public async post<G>(
    service: FetchingServices,
    url: string,
    body: object | null = null,
    timeout: number = 0,
    withToken: boolean = true,
    isShowServiceError: boolean = true,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    return await useNuxtApp()
      .$fetching.post<G>(service, url, body, withToken, timeout, responseType)
      .then((resp) => {
        if (resp.status && this.resIsNotOk(resp.status) && isShowServiceError) {
          this.showServiceError(resp.status, service);
        }
        return resp;
      })
      .catch((err) => {
        this.showServiceCatchError();
        throw err;
      });
  }
  public async put<G>(
    service: FetchingServices,
    url: string,
    body: object | null = null,
    timeout: number = 0,
    withToken: boolean = true,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    return await useNuxtApp()
      .$fetching.put<G>(service, url, body, withToken, timeout, responseType)
      .then((resp) => {
        if (resp.status && this.resIsNotOk(resp.status)) {
          this.showServiceError(resp.status, service);
        }
        return resp;
      })
      .catch((err) => {
        this.showServiceCatchError();
        throw err;
      });
  }
  public async delete<G>(
    service: FetchingServices,
    url: string,
    body: object | null = null,
    timeout: number = 0,
    withToken: boolean = true,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    return await useNuxtApp()
      .$fetching.delete<G>(service, url, body, withToken, timeout, responseType)
      .then((resp) => {
        if (resp.status && this.resIsNotOk(resp.status)) {
          this.showServiceError(resp.status, service);
        }
        return resp;
      })
      .catch((err) => {
        this.showServiceCatchError();
        throw err;
      });
  }
  public async patch<G>(
    service: FetchingServices,
    url: string,
    body: object,
    timeout: number = 0,
    withToken: boolean = true,
    responseType: string | null = null
  ): Promise<FetchingResponse<G>> {
    return await useNuxtApp()
      .$fetching.patch<G>(service, url, body, withToken, timeout, responseType)
      .then((resp) => {
        if (resp.status && this.resIsNotOk(resp.status)) {
          this.showServiceError(resp.status, service);
        }
        return resp;
      })
      .catch((err) => {
        this.showServiceCatchError();
        throw err;
      });
  }

  public showServiceError(status: number, service: string) {
    let message = 'canNotConnectToService';

    // Handle Business Error
    if (status >= 1000) {
      message = service + status;
    }
    // Handle Timeout Error
    if (status === 408) {
      message = 'Request Timeout';
    }

    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'stage'
    ) {
      if (status === 400) {
        message = 'Bad Request';
      }
      if (status === 401) {
        message = 'Unauthorized';
      }
      if (status === 403) {
        message = 'Forbidden';
      }
      if (status === 404) {
        message = 'Api Not Found';
      }
    }

    useToastStore().showAlert({
      title: message,
      showCloseButton: true,
      timerProgressBar: true,
      type: 'error',
    });
  }
  public showServiceCatchError() {
    useToastStore().showAlert({
      title: 'canNotConnectToServer',
      showCloseButton: true,
      timerProgressBar: true,
      type: 'error',
    });
  }

  public store<T>(name: string, obj?: T | null): T;
  public store<T>(name: string): T;
  public store<T>(name: string, obj: T | null = null): T {
    if (obj) return storeFactory<T>(name)().set(obj);
    else return storeFactory<T>(name)().get();
  }

  private resIsNotOk(status: number) {
    return !status.toString().startsWith('2');
  }
}

export const useBase = new Base();
